import {
  add,
  addNum,
  call,
  constant,
  dependsOn,
  type FnName,
  HALF,
  isExactNum,
  isIntegerNum,
  isNum,
  isNumEqual,
  keyOf,
  MINUS_ONE,
  mul,
  mulNum,
  type Node,
  type NumNode,
  nodeCount,
  num,
  numValue,
  ONE,
  pow,
  powNumInteger,
  substitute,
  variablesOf,
  ZERO,
} from "@/lib/tool-kit/calculus/ast";
import {
  polyDivide,
  polyFromNode,
  polyToNode,
} from "@/lib/tool-kit/calculus/polynomial";

/**
 * Algebraic simplification to a compact, conventional form.
 *
 * It combines like terms and like bases, folds exact numbers, applies the power and
 * logarithm identities that hold for every real input where both sides are defined, and
 * evaluates a few functions exactly (sin(π/6) = 1/2, ln(e) = 1, cos(0) = 1). It does not
 * expand or factor in general: 6x(x²+1)² stays factored. Two exceptions make derivatives
 * read like a textbook answer — a sum that is a polynomial is expanded and collected,
 * and the numerator of a polynomial fraction is expanded.
 *
 * Every rewrite preserves the value wherever the original was defined. (Cancelling a
 * common factor, as in (x+1)/(x+1) = 1, can enlarge the domain at the point where that
 * factor is zero, as in every computer algebra system.)
 */

const MAX_PASSES = 10;
const MAX_EXPANDED_TERMS = 300;
const MAX_EXPANDED_POWER = 12;
const MAX_RESHAPE = 4;

/** How many nested rounds of fraction tidying are running (guards against oscillation). */
let reshapeDepth = 0;

/* ------------------------------------------------------------- helpers */

function splitCoeff(node: Node): { coeff: NumNode; rest: Node[] } {
  if (node.kind === "num") {
    return { coeff: node, rest: [] };
  }
  if (node.kind === "mul") {
    let coeff: NumNode = ONE;
    const rest: Node[] = [];
    for (const factor of node.factors) {
      if (factor.kind === "num") {
        coeff = mulNum(coeff, factor);
      } else {
        rest.push(factor);
      }
    }
    return { coeff, rest };
  }
  return { coeff: ONE, rest: [node] };
}

function product(coeff: NumNode, rest: Node[]): Node {
  if (rest.length === 0) {
    return coeff;
  }
  if (isNumEqual(coeff, 1)) {
    return rest.length === 1 ? rest[0] : mul(...rest);
  }
  return mul(coeff, ...rest);
}

function hasVariable(node: Node): boolean {
  return variablesOf(node).length > 0;
}

function isE(node: Node): boolean {
  return node.kind === "const" && node.name === "e";
}

function isPositiveKnown(node: Node): boolean {
  if (node.kind === "num") {
    return numValue(node) > 0;
  }
  return node.kind === "const";
}

/* ---------------------------------------------------- exact trigonometry */

const SQRT2_OVER_2 = mul(num(1, 2), pow(num(2), HALF));
const SQRT3_OVER_2 = mul(num(1, 2), pow(num(3), HALF));

/** sin of m·15° for m in {0, 2, 3, 4, 6} (0°, 30°, 45°, 60°, 90°). */
function sinFirstQuadrant(m: number): Node {
  switch (m) {
    case 0:
      return ZERO;
    case 2:
      return num(1, 2);
    case 3:
      return SQRT2_OVER_2;
    case 4:
      return SQRT3_OVER_2;
    default:
      return ONE;
  }
}

function sinMultiple(m: number): Node {
  const k = ((m % 24) + 24) % 24;
  if (k >= 12) {
    return simplifyMul([MINUS_ONE, sinMultiple(k - 12)]);
  }
  return sinFirstQuadrant(k > 6 ? 12 - k : k);
}

/** The multiple m of 15° that an argument equals, if it is one we can evaluate. */
function degreesOf15(arg: Node): number | null {
  let t: NumNode | null = null;
  if (isNumEqual(arg, 0)) {
    t = ZERO;
  } else if (arg.kind === "const" && arg.name === "pi") {
    t = ONE;
  } else if (arg.kind === "mul" && arg.factors.length === 2) {
    const [c, p] = arg.factors;
    if (isExactNum(c) && p.kind === "const" && p.name === "pi") {
      t = c;
    }
  }
  if (t === null) {
    return null;
  }
  const scaled = t.n * 12;
  if (scaled % t.d !== 0) {
    return null;
  }
  const m = scaled / t.d;
  return m % 2 === 0 || m % 3 === 0 ? m : null;
}

function reciprocal(node: Node): Node | null {
  return isNumEqual(node, 0) ? null : simplifyPow(node, MINUS_ONE);
}

function exactTrig(fn: FnName, arg: Node): Node | null {
  const m = degreesOf15(arg);
  if (m === null) {
    return null;
  }
  const s = sinMultiple(m);
  const c = sinMultiple(m + 6);
  switch (fn) {
    case "sin":
      return s;
    case "cos":
      return c;
    case "tan": {
      const inverse = reciprocal(c);
      return inverse ? simplifyMul([s, inverse]) : null;
    }
    case "sec":
      return reciprocal(c);
    case "csc":
      return reciprocal(s);
    case "cot": {
      const inverse = reciprocal(s);
      return inverse ? simplifyMul([c, inverse]) : null;
    }
    default:
      return null;
  }
}

const ODD: ReadonlySet<FnName> = new Set([
  "sin",
  "tan",
  "csc",
  "cot",
  "asin",
  "atan",
  "sinh",
  "tanh",
]);
const EVEN: ReadonlySet<FnName> = new Set(["cos", "sec", "cosh", "abs"]);

function inverseValue(fn: FnName, arg: Node): Node | null {
  if (!isExactNum(arg)) {
    return null;
  }
  const value = numValue(arg);
  const pi = constant("pi");
  if (fn === "atan") {
    if (value === 0) {
      return ZERO;
    }
    return value === 1 ? simplifyMul([num(1, 4), pi]) : null;
  }
  if (fn === "asin") {
    if (value === 0) {
      return ZERO;
    }
    if (value === 1) {
      return simplifyMul([HALF, pi]);
    }
    return value === 0.5 ? simplifyMul([num(1, 6), pi]) : null;
  }
  if (fn === "acos") {
    if (value === 1) {
      return ZERO;
    }
    if (value === 0) {
      return simplifyMul([HALF, pi]);
    }
    return value === 0.5 ? simplifyMul([num(1, 3), pi]) : null;
  }
  return null;
}

/* --------------------------------------------------------------- calls */

function isZero(node: Node): boolean {
  return isNumEqual(node, 0);
}

/** Values of ln, log and the hyperbolic functions at 0, 1, e and 10. */
function specialValue(fn: FnName, arg: Node): Node | null {
  switch (fn) {
    case "ln":
      if (isNumEqual(arg, 1)) {
        return ZERO;
      }
      if (isE(arg)) {
        return ONE;
      }
      return arg.kind === "pow" && isE(arg.base) ? arg.exp : null;
    case "log":
      if (isNumEqual(arg, 1)) {
        return ZERO;
      }
      return isNumEqual(arg, 10) ? ONE : null;
    case "cosh":
      return isZero(arg) ? ONE : null;
    case "sinh":
    case "tanh":
      return isZero(arg) ? ZERO : null;
    default:
      return null;
  }
}

/** |number|, |abs(u)|, |u^even| need no bars to evaluate. */
function absValue(arg: Node): Node | null {
  if (arg.kind === "num") {
    return num(Math.abs(arg.n), arg.d);
  }
  if (arg.kind === "call" && arg.fn === "abs") {
    return arg;
  }
  if (arg.kind === "pow" && isIntegerNum(arg.exp) && arg.exp.n % 2 === 0) {
    return arg;
  }
  return null;
}

/** Pull a negative coefficient out of an odd or even function: sin(−2x) = −sin(2x). */
function applyParity(fn: FnName, arg: Node): Node | null {
  const { coeff, rest } = splitCoeff(arg);
  if (numValue(coeff) < 0 && (ODD.has(fn) || EVEN.has(fn))) {
    const positive = simplifyMul([num(-coeff.n, coeff.d), ...rest]);
    const inner = simplifyCall(fn, positive);
    return ODD.has(fn) ? simplifyMul([MINUS_ONE, inner]) : inner;
  }
  if (fn === "abs" && numValue(coeff) !== 1 && rest.length > 0) {
    return simplifyMul([
      num(Math.abs(coeff.n), coeff.d),
      simplifyCall("abs", product(ONE, rest)),
    ]);
  }
  return null;
}

function simplifyCall(fn: FnName, arg: Node): Node {
  const exact =
    exactTrig(fn, arg) ??
    inverseValue(fn, arg) ??
    specialValue(fn, arg) ??
    (fn === "abs" ? absValue(arg) : null) ??
    applyParity(fn, arg);
  return exact ?? call(fn, arg);
}

/* ---------------------------------------------------------------- powers */

function integerRoot(m: number, q: number): number | null {
  const r = Math.round(m ** (1 / q));
  return r ** q === m ? r : null;
}

/** An exact rational power of an exact rational, when the result is rational. */
function numberPower(base: NumNode, exp: NumNode): Node | null {
  if (!(isExactNum(base) && isExactNum(exp))) {
    return null;
  }
  if (exp.d === 1) {
    return powNumInteger(base, exp.n);
  }
  if (base.n < 0 && exp.d % 2 === 0) {
    return null;
  }
  const rootN = integerRoot(Math.abs(base.n), exp.d);
  const rootD = integerRoot(base.d, exp.d);
  if (rootN === null || rootD === null) {
    return null;
  }
  const root = num(base.n < 0 ? -rootN : rootN, rootD);
  return powNumInteger(root, exp.n);
}

function numericPower(base: Node, exp: Node): Node | null {
  if (!(isNum(base) && isNum(exp))) {
    return null;
  }
  const exact = numberPower(base, exp);
  if (exact) {
    return exact;
  }
  return numValue(base) === 0 && numValue(exp) > 0 ? ZERO : null;
}

/** (b^a)^c, (b·c)^n, |u|^even and e^(ln u). */
function structuralPower(base: Node, exp: Node): Node | null {
  if (
    base.kind === "pow" &&
    (isIntegerNum(exp) || isPositiveKnown(base.base))
  ) {
    return simplifyPow(base.base, simplifyMul([base.exp, exp]));
  }
  if (base.kind === "mul" && isIntegerNum(exp)) {
    return simplifyMul(base.factors.map((f) => simplifyPow(f, exp)));
  }
  if (isE(base)) {
    return exponentialOfLog(exp);
  }
  if (
    base.kind === "call" &&
    base.fn === "abs" &&
    isIntegerNum(exp) &&
    exp.n % 2 === 0
  ) {
    return simplifyPow(base.arg, exp);
  }
  return null;
}

function simplifyPow(base: Node, exp: Node): Node {
  if (isNum(exp) && numValue(exp) === 0) {
    return ONE;
  }
  if (isNum(exp) && numValue(exp) === 1) {
    return base;
  }
  if (isNumEqual(base, 1)) {
    return ONE;
  }
  return (
    numericPower(base, exp) ?? structuralPower(base, exp) ?? pow(base, exp)
  );
}

/** e^(ln u) = u and e^(k ln u) = u^k. */
function exponentialOfLog(exp: Node): Node | null {
  if (exp.kind === "call" && exp.fn === "ln") {
    return exp.arg;
  }
  if (exp.kind === "mul") {
    const at = exp.factors.findIndex((f) => f.kind === "call" && f.fn === "ln");
    const logged = exp.factors[at];
    const others = exp.factors.filter((_, i) => i !== at);
    if (
      logged?.kind === "call" &&
      !others.some((f) => f.kind === "call" && f.fn === "ln")
    ) {
      return simplifyPow(logged.arg, simplifyMul(others));
    }
  }
  return null;
}

/* -------------------------------------------------------------- products */

function rankOf(factor: Node): number {
  const core = factor.kind === "pow" ? factor.base : factor;
  const symbolicExponent = factor.kind === "pow" && factor.exp.kind !== "num";
  if (core.kind === "num" || core.kind === "const") {
    return symbolicExponent ? 3 : 1;
  }
  if (core.kind === "var") {
    return 2;
  }
  if (core.kind === "add") {
    return 4;
  }
  return core.kind === "call" ? 5 : 3;
}

function sortFactors(factors: Node[]): Node[] {
  return factors
    .map((factor, index) => ({ factor, index, rank: rankOf(factor) }))
    .sort((a, b) => {
      if (a.rank !== b.rank) {
        return a.rank - b.rank;
      }
      if (a.rank === 2) {
        const left = variablesOf(a.factor)[0] ?? "";
        const right = variablesOf(b.factor)[0] ?? "";
        if (left !== right) {
          return left < right ? -1 : 1;
        }
      }
      return a.index - b.index;
    })
    .map((entry) => entry.factor);
}

function flattenFactors(input: Node[], out: Node[] = []): Node[] {
  for (const factor of input) {
    if (factor.kind === "mul") {
      flattenFactors(factor.factors, out);
    } else {
      out.push(factor);
    }
  }
  return out;
}

function isDenominatorFactor(factor: Node): boolean {
  return (
    factor.kind === "pow" &&
    factor.exp.kind === "num" &&
    numValue(factor.exp) < 0 &&
    hasVariable(factor.base)
  );
}

function gcdInt(a: number, b: number): number {
  return b === 0 ? Math.abs(a) : gcdInt(b, a % b);
}

/** The rational content of a list of coefficients: 2, −4, −6 have content 2. */
function contentOf(coefficients: NumNode[]): NumNode {
  let g = 0;
  let l = 1;
  for (const coeff of coefficients) {
    if (!isExactNum(coeff)) {
      return ONE;
    }
    g = gcdInt(g, coeff.n);
    l = (l * coeff.d) / gcdInt(l, coeff.d);
  }
  return g === 0 ? ONE : num(g, l);
}

interface TermParts {
  coeff: NumNode;
  bases: Map<string, { base: Node; exp: Node }>;
}

/** A term as a coefficient and its (base → exponent) factors; null if it has a denominator. */
function termParts(term: Node): TermParts | null {
  const { coeff, rest } = splitCoeff(term);
  const bases = new Map<string, { base: Node; exp: Node }>();
  for (const factor of rest) {
    const base = factor.kind === "pow" ? factor.base : factor;
    const exp = factor.kind === "pow" ? factor.exp : ONE;
    if (exp.kind === "num" && numValue(exp) < 0) {
      return null;
    }
    bases.set(keyOf(base), { base, exp });
  }
  return { coeff, bases };
}

interface Common {
  base: Node;
  exp: Node;
}

/** The bases every term shares, each with the smallest exponent (or the equal symbolic one). */
function commonBases(parts: TermParts[]): Map<string, Common> {
  const common = new Map<string, Common>();
  for (const [key, entry] of parts[0].bases) {
    const exps = parts.map((p) => p.bases.get(key)?.exp);
    if (exps.some((e) => e === undefined)) {
      continue;
    }
    const nodes = exps as Node[];
    if (nodes.every((e) => e.kind === "num" && isExactNum(e))) {
      const smallest = Math.min(...nodes.map((e) => numValue(e as NumNode)));
      if (smallest > 0) {
        common.set(key, { base: entry.base, exp: num(smallest) });
      }
    } else if (nodes.every((e) => keyOf(e) === keyOf(nodes[0]))) {
      common.set(key, { base: entry.base, exp: nodes[0] });
    }
  }
  return common;
}

function reduceTerm(
  part: TermParts,
  common: Map<string, Common>,
  divisor: NumNode
): Node {
  const factors: Node[] = [];
  for (const [key, entry] of part.bases) {
    const shared = common.get(key);
    const exp = shared
      ? simplifyAdd([entry.exp, simplifyMul([MINUS_ONE, shared.exp])])
      : entry.exp;
    if (!isNumEqual(exp, 0)) {
      factors.push(pow(entry.base, exp));
    }
  }
  return simplifyMul([
    mulNum(part.coeff, num(divisor.d, divisor.n)),
    ...factors,
  ]);
}

/**
 * Pull what every term of a sum shares in front of it: a numeric content and common
 * factors, e.g. x eˣ + eˣ → eˣ(x + 1), 2x⁵ − 4x³ → 2x³(x² − 2). With `allowPlain`
 * false it only acts when some shared factor is more than a plain variable power, so a
 * polynomial such as 3x² − 2x is left alone.
 */
function factorCommon(
  terms: Node[],
  allowPlain: boolean
): { factors: Node[]; rest: Node } | null {
  const parts = terms.map(termParts);
  if (terms.length < 2 || parts.some((p) => p === null)) {
    return null;
  }
  const all = parts as TermParts[];
  const common = commonBases(all);
  const worthIt =
    common.size > 0 &&
    (allowPlain || [...common.values()].some((c) => c.base.kind !== "var"));
  const content = contentOf(all.map((p) => p.coeff));
  const negative = all.every((p) => numValue(p.coeff) < 0);
  const divisor = negative ? num(-content.n, content.d) : content;
  if (!worthIt && numValue(divisor) === 1) {
    return null;
  }
  if (!(worthIt || allowPlain)) {
    return null;
  }
  const rest = simplifyAdd(all.map((p) => reduceTerm(p, common, divisor)));
  const factors = [
    ...(numValue(divisor) === 1 ? [] : [divisor]),
    ...[...common.values()].map((c) => simplifyPow(c.base, c.exp)),
  ];
  return { factors, rest };
}

/** A denominator factor base^(−k) after `remaining` of its k powers are left uncancelled. */
function reducedFactor(
  factor: Extract<Node, { kind: "pow" }>,
  remaining: number
): Node {
  if (remaining === -(factor.exp as NumNode).n) {
    return factor;
  }
  return remaining === 0 ? ONE : pow(factor.base, num(-remaining));
}

/** Cancel factors the denominator shares with an expanded numerator. */
function cancelFactors(
  numerator: Node,
  denominator: Node[]
): { numerator: Node; denominator: Node[] } {
  const names = variablesOf(numerator);
  if (names.length !== 1) {
    return { numerator, denominator };
  }
  let poly = polyFromNode(numerator, names[0]);
  if (poly === null) {
    return { numerator, denominator };
  }
  const kept: Node[] = [];
  for (const factor of denominator) {
    if (
      !(
        poly &&
        factor.kind === "pow" &&
        isIntegerNum(factor.exp) &&
        variablesOf(factor.base).join() === names[0]
      )
    ) {
      kept.push(factor);
      continue;
    }
    const divisor = polyFromNode(factor.base, names[0]);
    let remaining = -factor.exp.n;
    while (divisor && divisor.length > 1 && remaining > 0) {
      const division = polyDivide(poly, divisor);
      if (division === null || division.remainder.length > 0) {
        break;
      }
      poly = division.quotient;
      remaining -= 1;
    }
    kept.push(reducedFactor(factor, remaining));
  }
  return {
    numerator: poly ? polyToNode(poly, names[0]) : numerator,
    denominator: kept,
  };
}

/**
 * Tidy a fraction: expand its numerator, cancel what it shares with the denominator,
 * and pull common factors out in front so powers can cancel on the next round.
 * Returns null when nothing changes.
 */
function reshapeFraction(factors: Node[]): Node[] | null {
  const numerator = factors.filter((f) => !isDenominatorFactor(f));
  const denominator = factors.filter(isDenominatorFactor);
  if (
    denominator.length === 0 ||
    !numerator.some((f) => f.kind === "add") ||
    (numerator.length === 1 && numerator[0].kind === "pow")
  ) {
    return null;
  }
  const expanded = expandedOrNull(mul(...numerator));
  if (expanded === null) {
    return null;
  }
  const cancelled = isPolynomialLike(expanded)
    ? cancelFactors(expanded, denominator)
    : { numerator: expanded, denominator };
  let top = simplify(cancelled.numerator);
  let pulled: Node[] = [];
  if (top.kind === "add") {
    const common = factorCommon(top.terms, true);
    if (common) {
      pulled = common.factors;
      top = common.rest;
    }
  }
  const out = [
    ...pulled,
    ...(top.kind === "mul" ? top.factors : [top]),
    ...cancelled.denominator,
  ];
  return keyOf(mul(...out)) === keyOf(mul(...factors)) ? null : out;
}

interface Group {
  base: Node;
  exps: Node[];
}

function groupBases(flat: Node[]): { coeff: NumNode; groups: Group[] } {
  let coeff: NumNode = ONE;
  const groups: Group[] = [];
  const index = new Map<string, number>();
  for (const factor of flat) {
    if (factor.kind === "num") {
      coeff = mulNum(coeff, factor);
      continue;
    }
    const base = factor.kind === "pow" ? factor.base : factor;
    const exp = factor.kind === "pow" ? factor.exp : ONE;
    const key = keyOf(base);
    const at = index.get(key);
    if (at === undefined) {
      index.set(key, groups.length);
      groups.push({ base, exps: [exp] });
    } else {
      groups[at].exps.push(exp);
    }
  }
  return { coeff, groups };
}

/** 1/√2 → (1/2)·√2: a root of a whole number in a denominator moves to the numerator. */
function rationalizeRoot(
  base: Node,
  exp: Node
): { factor: NumNode; piece: Node } | null {
  if (
    base.kind === "num" &&
    isExactNum(base) &&
    base.d === 1 &&
    base.n > 1 &&
    exp.kind === "num" &&
    isExactNum(exp) &&
    exp.d > 1 &&
    numValue(exp) > -1 &&
    numValue(exp) < 0
  ) {
    return {
      factor: num(1, base.n),
      piece: pow(base, num(exp.n + exp.d, exp.d)),
    };
  }
  return null;
}

function simplifyMul(input: Node[]): Node {
  const { coeff: first, groups } = groupBases(flattenFactors(input));
  let coeff = first;
  if (numValue(coeff) === 0) {
    return ZERO;
  }
  const pieces: Node[] = [];
  let regroup = false;
  for (const group of groups) {
    const exp =
      group.exps.length === 1 ? group.exps[0] : simplifyAdd(group.exps);
    const rationalized = rationalizeRoot(group.base, exp);
    if (rationalized) {
      coeff = mulNum(coeff, rationalized.factor);
    }
    const piece = rationalized
      ? rationalized.piece
      : simplifyPow(group.base, exp);
    if (piece.kind === "num") {
      coeff = mulNum(coeff, piece);
    } else if (piece.kind === "mul") {
      pieces.push(...piece.factors);
      regroup = true;
    } else {
      pieces.push(piece);
    }
  }
  if (regroup) {
    return simplifyMul([coeff, ...pieces]);
  }
  if (numValue(coeff) === 0) {
    return ZERO;
  }
  const reshaped = reshapeDepth < MAX_RESHAPE ? reshapeFraction(pieces) : null;
  if (reshaped) {
    reshapeDepth += 1;
    try {
      return simplifyMul([coeff, ...reshaped]);
    } finally {
      reshapeDepth -= 1;
    }
  }
  return product(coeff, sortFactors(pieces));
}

/* ------------------------------------------------------------------ sums */

interface Item {
  coeff: NumNode;
  rest: Node[];
}

function pushTerm(out: Node[], term: Node) {
  if (term.kind === "add") {
    for (const inner of term.terms) {
      pushTerm(out, inner);
    }
    return;
  }
  if (term.kind === "mul") {
    // c·(a + b) inside a sum is c·a + c·b.
    const { coeff, rest } = splitCoeff(term);
    if (rest.length === 1 && rest[0].kind === "add" && !isNumEqual(coeff, 1)) {
      for (const inner of rest[0].terms) {
        pushTerm(out, simplifyMul([coeff, inner]));
      }
      return;
    }
  }
  out.push(term);
}

function collectItems(flat: Node[]): { constant: NumNode; items: Item[] } {
  let constantTerm: NumNode = ZERO;
  const items: Item[] = [];
  const index = new Map<string, number>();
  for (const term of flat) {
    if (term.kind === "num") {
      constantTerm = addNum(constantTerm, term);
      continue;
    }
    const { coeff, rest } = splitCoeff(term);
    const key = keyOf(mul(...rest));
    const at = index.get(key);
    if (at === undefined) {
      index.set(key, items.length);
      items.push({ coeff, rest });
    } else {
      items[at].coeff = addNum(items[at].coeff, coeff);
    }
  }
  return {
    constant: constantTerm,
    items: items.filter((i) => numValue(i.coeff) !== 0),
  };
}

function squaredCall(factor: Node, fn: FnName): Node | null {
  if (
    factor.kind === "pow" &&
    isNumEqual(factor.exp, 2) &&
    factor.base.kind === "call" &&
    factor.base.fn === fn
  ) {
    return factor.base.arg;
  }
  return null;
}

/** sin²u + cos²u = 1, with any common coefficient and companion factors. */
function applyPythagoras(items: Item[]): { items: Item[]; extra: NumNode } {
  const live = [...items];
  const removed = new Set<number>();
  let extra: NumNode = ZERO;
  for (let i = 0; i < live.length; i += 1) {
    if (removed.has(i)) {
      continue;
    }
    const item = live[i];
    const at = item.rest.findIndex((f) => squaredCall(f, "sin") !== null);
    if (at < 0) {
      continue;
    }
    const argument = squaredCall(item.rest[at], "sin") as Node;
    const companion = item.rest.map((f, k) =>
      k === at ? pow(call("cos", argument), num(2)) : f
    );
    const wanted = keyOf(mul(...companion));
    const j = live.findIndex(
      (other, k) =>
        k !== i &&
        !removed.has(k) &&
        numValue(other.coeff) === numValue(item.coeff) &&
        keyOf(mul(...other.rest)) === wanted
    );
    if (j < 0) {
      continue;
    }
    removed.add(j);
    const without = item.rest.filter((_, k) => k !== at);
    if (without.length === 0) {
      extra = addNum(extra, item.coeff);
      removed.add(i);
    } else {
      live[i] = { coeff: item.coeff, rest: without };
    }
  }
  return { items: live.filter((_, k) => !removed.has(k)), extra };
}

/** (name, degree) when a term is c·x^k with k a non-negative integer; constants are degree 0. */
function monomialDegree(
  term: Node
): { name: string | null; degree: number } | null {
  if (term.kind === "num") {
    return { name: null, degree: 0 };
  }
  const { rest } = splitCoeff(term);
  if (rest.length !== 1) {
    return null;
  }
  const [only] = rest;
  if (only.kind === "var") {
    return { name: only.name, degree: 1 };
  }
  if (
    only.kind === "pow" &&
    only.base.kind === "var" &&
    isIntegerNum(only.exp) &&
    only.exp.n >= 0
  ) {
    return { name: only.base.name, degree: only.exp.n };
  }
  return null;
}

function leadsNegative(term: Node): boolean {
  return numValue(splitCoeff(term).coeff) < 0;
}

/** Polynomials by descending degree; otherwise keep the order, but do not lead with a minus. */
function orderTerms(terms: Node[]): Node[] {
  const degrees = terms.map(monomialDegree);
  if (degrees.every((d) => d !== null)) {
    const names = new Set(
      degrees.map((d) => d?.name).filter((n) => n !== null)
    );
    if (names.size <= 1) {
      const sorted = terms
        .map((term, i) => ({ term, degree: degrees[i]?.degree ?? 0 }))
        .sort((a, b) => b.degree - a.degree)
        .map((entry) => entry.term);
      return sorted.length === 2 &&
        leadsNegative(sorted[0]) &&
        !leadsNegative(sorted[1])
        ? [sorted[1], sorted[0]]
        : sorted;
    }
  }
  if (terms.length > 1 && leadsNegative(terms[0])) {
    const at = terms.findIndex((term) => !leadsNegative(term));
    if (at > 0) {
      return [terms[at], ...terms.filter((_, i) => i !== at)];
    }
  }
  return terms;
}

function simplifyAdd(input: Node[]): Node {
  const flat: Node[] = [];
  for (const term of input) {
    pushTerm(flat, term);
  }
  const collected = collectItems(flat);
  const paired = applyPythagoras(collected.items);
  const constantTerm = addNum(collected.constant, paired.extra);
  const terms = paired.items
    .filter((item) => numValue(item.coeff) !== 0)
    .map((item) => product(item.coeff, item.rest));
  const ordered = orderTerms(
    numValue(constantTerm) === 0 ? terms : [...terms, constantTerm]
  );
  if (ordered.length === 0) {
    return ZERO;
  }
  return ordered.length === 1 ? ordered[0] : add(...ordered);
}

/* ------------------------------------------------------------- the pass */

function pass(node: Node): Node {
  switch (node.kind) {
    case "add":
      return simplifyAdd(node.terms.map(pass));
    case "mul":
      return simplifyMul(node.factors.map(pass));
    case "pow":
      return simplifyPow(pass(node.base), pass(node.exp));
    case "call":
      return simplifyCall(node.fn, pass(node.arg));
    default:
      return node;
  }
}

/** Simplify to a fixed point. */
export function simplify(node: Node): Node {
  let current = node;
  for (let i = 0; i < MAX_PASSES; i += 1) {
    const next = pass(current);
    if (keyOf(next) === keyOf(current)) {
      return next;
    }
    current = next;
  }
  return current;
}

/* ------------------------------------------------------------- expansion */

class TooLarge extends Error {}

function termsOf(node: Node): Node[] {
  return node.kind === "add" ? node.terms : [node];
}

function distribute(a: Node, b: Node): Node {
  const left = termsOf(a);
  const right = termsOf(b);
  if (left.length * right.length > MAX_EXPANDED_TERMS) {
    throw new TooLarge();
  }
  const products: Node[] = [];
  for (const x of left) {
    for (const y of right) {
      products.push(mul(x, y));
    }
  }
  return products.length === 1 ? products[0] : add(...products);
}

function expandRaw(node: Node): Node {
  switch (node.kind) {
    case "add":
      return add(...node.terms.map(expandRaw));
    case "mul": {
      let acc: Node = ONE;
      for (const factor of node.factors) {
        acc = distribute(acc, expandRaw(factor));
      }
      return acc;
    }
    case "pow": {
      const base = expandRaw(node.base);
      if (
        base.kind === "add" &&
        isIntegerNum(node.exp) &&
        node.exp.n >= 2 &&
        node.exp.n <= MAX_EXPANDED_POWER
      ) {
        let acc: Node = base;
        for (let i = 1; i < node.exp.n; i += 1) {
          acc = distribute(acc, base);
        }
        return acc;
      }
      return pow(base, node.exp);
    }
    default:
      return node;
  }
}

function expandedOrNull(node: Node): Node | null {
  try {
    return simplify(expandRaw(node));
  } catch (error) {
    if (error instanceof TooLarge) {
      return null;
    }
    throw error;
  }
}

/** Multiply out products of sums and small integer powers of sums, then simplify. */
export function expand(node: Node): Node {
  return expandedOrNull(simplify(node)) ?? simplify(node);
}

/** A polynomial in its variables (constants may be any closed-form number). */
export function isPolynomialLike(node: Node): boolean {
  switch (node.kind) {
    case "num":
    case "const":
    case "var":
      return true;
    case "add":
      return node.terms.every(isPolynomialLike);
    case "mul":
      return node.factors.every(isPolynomialLike);
    case "pow":
      return (
        !hasVariable(node) ||
        (isIntegerNum(node.exp) &&
          node.exp.n >= 0 &&
          isPolynomialLike(node.base))
      );
    default:
      return !hasVariable(node);
  }
}

/** c·(a + b) with nothing else in the product becomes c·a + c·b. */
function distributeLeadingCoefficient(node: Node): Node {
  if (
    node.kind === "mul" &&
    node.factors.length === 2 &&
    node.factors[0].kind === "num" &&
    node.factors[1].kind === "add"
  ) {
    const [coefficient, sum] = node.factors;
    return simplify(add(...sum.terms.map((t) => mul(coefficient, t))));
  }
  return node;
}

/**
 * The form a final answer is shown in: simplified; a polynomial sum expanded and
 * collected (2x(x − 1) + x² + 1 reads 3x² − 2x + 1); any other sum with a common
 * factor factored (x eˣ + eˣ reads (x + 1)eˣ).
 */
export function finalize(node: Node): Node {
  const simple = distributeLeadingCoefficient(simplify(node));
  if (simple.kind !== "add") {
    return simple;
  }
  if (isPolynomialLike(simple)) {
    const expanded = expandedOrNull(simple);
    return expanded && nodeCount(expanded) <= 4 * nodeCount(simple) + 20
      ? expanded
      : simple;
  }
  const factored = factorCommon(simple.terms, false);
  return factored ? simplify(mul(...factored.factors, factored.rest)) : simple;
}

/** Exact value of an expression at a point: substitute, then simplify. */
export function simplifyAt(node: Node, name: string, value: Node): Node {
  return dependsOn(node, name)
    ? simplify(substitute(node, name, value))
    : simplify(node);
}
